/**
 * Sync engine: keeps the local tables in step with the server.
 *
 * - Edits apply locally at once and go to POST /api/mutations through an
 *   ordered outbox that retries on network errors (the server ignores
 *   repeats of the same clientMutationId).
 * - The server streams every committed change over /ws in commit order.
 *   Deltas apply to the tables, then still-pending local edits are replayed
 *   on top, so a slow echo never undoes a newer local edit.
 * - On reconnect the client sends the last seq it saw and the server replays
 *   what was missed; if that's not possible it asks for a full reload.
 */
import { toast } from "sonner";
import { ApiError, get, post } from "./api";
import { presence } from "./presence";
import { getTable, indexById, queryClient, setTable } from "./store";
import { ENTITY_NAMES, type BatchResult, type EntityName, type Id, type Op } from "./types";

export type SyncStatus = "offline" | "connecting" | "live";

interface Delta {
  type: "delta";
  seq: number;
  entity: EntityName;
  entityId: Id;
  /** hide/show: the row left or joined what this person can see (team move, made private or shared). */
  op: "insert" | "update" | "delete" | "hide" | "show";
  patch: Record<string, unknown>;
  actorId: Id | null;
  clientMutationId: Id | null;
}

interface Pending {
  cmid: string;
  op: Op;
  confirmedAt?: number;
}

interface OutboxEntry {
  cmid: string;
  ops: Op[];
  attempts: number;
  resolve: (r: BatchResult) => void;
  reject: (e: unknown) => void;
}

interface Bootstrap {
  seq: number;
  userId: Id;
  role: string;
  entities: Record<string, Array<{ id: Id }>>;
}

const now = () => new Date().toISOString();
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
export const uuid = () => crypto.randomUUID();

/** Defaults for rows created locally before the server's copy arrives. */
function defaults(entity: EntityName, me: Id | null): Record<string, unknown> {
  switch (entity) {
    case "issue":
      return {
        number: null, description: null, descriptionText: "", priority: 0, estimate: null, assigneeId: null, creatorId: me, parentId: null,
        projectId: null, milestoneId: null, cycleId: null, labelIds: [], subscriberIds: me ? [me] : [], upvoterIds: [], downvoterIds: [], dueDate: null, sortKey: "a0", source: null,
        triagedAt: null, snoozedUntil: null, slaDueAt: null, slaBreachedAt: null, startedAt: null, completedAt: null, canceledAt: null,
        stateChangedAt: now(), lastEditedBy: null, lastEditedAt: null,
      };
    case "comment":
      return { issueId: null, projectUpdateId: null, ideaId: null, documentId: null, projectId: null, parentId: null, teamId: null, authorId: me, body: null, bodyText: "", quote: null, resolvedAt: null, editedAt: null };
    case "project":
      return { summary: "", description: null, descriptionText: "", icon: null, color: "#5E6AD2", status: "planned", priority: 0, health: null, leadId: null, creatorId: me, memberIds: [], teamIds: [], labelIds: [], startDate: null, targetDate: null, updateCadenceDays: 7, lastUpdateAt: null, originIdeaId: null, sortKey: "a0", startedAt: null, completedAt: null, canceledAt: null };
    case "initiative":
      return { parentId: null, summary: "", description: null, descriptionText: "", icon: null, color: "#0F7B6C", status: "proposed", priority: 0, health: null, ownerId: null, creatorId: me, labelIds: [], projectIds: [], teamId: null, targetDate: null, updateCadenceDays: 14, lastUpdateAt: null, originIdeaId: null, sortKey: "a0", completedAt: null };
    case "idea":
      return { number: null, summary: "", description: null, descriptionText: "", status: "inbox", ownerId: me, creatorId: me, teamId: null, labelIds: [], voteUserIds: [], scoreModel: "ice", iceImpact: null, iceConfidence: null, iceEase: null, riceReach: null, riceImpact: null, riceConfidence: null, riceEffort: null, score: null, revisitOn: null, parkNote: "", promotedToType: null, promotedToId: null, promotedAt: null, sortKey: "a0" };
    case "document":
      return { title: "Untitled", icon: null, projectId: null, initiativeId: null, ideaId: null, teamId: null, creatorId: me, content: null, contentText: "", sortKey: "a0", lastEditedBy: null, lastEditedAt: null };
    case "sticky":
      return { userId: me, body: null, bodyText: "", color: "lavender", sortKey: "a0" };
    case "quickLink":
      return { userId: me, sortKey: "a0" };
    case "issueDraft":
      return { userId: me, teamId: null, title: "", description: null, descriptionText: "", data: {} };
    case "label":
      return { teamId: null, parentId: null, isGroup: false, color: "#8B8D98", description: "", scope: "issue" };
    case "view":
      return { description: "", icon: null, color: "#8B8D98", ownerId: me, teamId: null, shared: true, entity: "issue", filter: {}, display: {}, sortKey: "a0" };
    case "reaction":
      return { commentId: null, projectUpdateId: null, userId: me };
    case "favorite":
      return { userId: me, sortKey: "a0" };
    case "projectUpdate":
      return { projectId: null, initiativeId: null, body: null, bodyText: "", authorId: me };
    case "evidence":
      return { ideaId: null, issueId: null, projectId: null, customerId: null, teamId: null, kind: "quote", url: null, quote: "", source: "", creatorId: me };
    case "customer":
      return { domain: null, tier: null, revenueCents: 0, ownerId: null, notes: "" };
    case "dashboard":
      return { description: "", ownerId: me, teamId: null, shared: true, widgets: [], sortKey: "a0" };
    case "whatsappSubscription":
      return { userId: me, teamId: null, projectId: null, events: ["issue.created", "issue.completed", "project_update.created"] };
    default:
      return {};
  }
}

function applyOp(table: Record<Id, Record<string, unknown>>, op: Op, me: Id | null) {
  if (op.op === "action") return;
  const row = table[op.id];
  switch (op.op) {
    case "create":
      if (!row) table[op.id] = { id: op.id, createdAt: now(), updatedAt: now(), archivedAt: null, ...defaults(op.entity, me), ...op.data };
      else table[op.id] = { ...row, ...op.data };
      break;
    case "update":
      if (row) table[op.id] = { ...row, ...op.data, updatedAt: now() };
      break;
    case "add":
    case "remove": {
      if (!row) break;
      const current = (row[op.field] as Id[] | undefined) ?? [];
      const next = op.op === "add" ? [...new Set([...current, ...op.values])] : current.filter((v) => !op.values.includes(v));
      table[op.id] = { ...row, [op.field]: next.sort() };
      break;
    }
    case "archive":
      if (row) table[op.id] = { ...row, archivedAt: now() };
      break;
    case "unarchive":
      if (row) table[op.id] = { ...row, archivedAt: null };
      break;
    case "delete":
      delete table[op.id];
      break;
  }
}

function opEntity(op: Op): EntityName | null {
  return op.op === "action" ? null : op.entity;
}
function opId(op: Op): Id | null {
  return op.op === "action" ? null : op.id;
}

type Listener = () => void;

class SyncEngine {
  status: SyncStatus = "offline";
  lastSeq = 0;
  me: Id | null = null;
  ready = false;
  private ws: WebSocket | null = null;
  private started = false;
  private retry = 0;
  private resyncing = false;
  private pending: Pending[] = [];
  private outbox: OutboxEntry[] = [];
  private sending = false;
  private deltaQueue: Delta[] = [];
  private flushScheduled = false;
  private listeners = new Set<Listener>();
  private snapshot = { status: this.status as SyncStatus, pending: 0, lastSeq: 0 };

  // ---- state for the UI -----------------------------------------------------------------

  subscribe = (l: Listener) => {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  };
  getSnapshot = () => this.snapshot;
  private emit() {
    this.snapshot = { status: this.status, pending: this.outbox.length, lastSeq: this.lastSeq };
    this.listeners.forEach((l) => l());
  }

  // ---- loading ------------------------------------------------------------------------

  async bootstrap() {
    const b = await get<Bootstrap>("/api/bootstrap");
    this.me = b.userId;
    for (const name of ENTITY_NAMES) {
      const rows = b.entities[name];
      if (rows) setTable(name, () => indexById(rows) as never);
    }
    this.lastSeq = b.seq;
    this.replayAllPending();
    this.ready = true;
    this.emit();
    return b;
  }

  start() {
    if (this.started) return;
    this.started = true;
    this.connect();
    window.addEventListener("online", () => this.reconnectNow());
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") this.reconnectNow();
    });
  }

  stop() {
    this.started = false;
    this.ws?.close();
    this.ws = null;
    presence.attach(null);
  }

  private reconnectNow() {
    if (this.started && !this.ws && !this.resyncing) {
      this.retry = 0;
      this.connect();
    }
  }

  private connect() {
    if (!this.started || this.ws) return;
    this.status = "connecting";
    this.emit();
    const url = `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/ws`;
    const ws = new WebSocket(url);
    this.ws = ws;
    ws.onopen = () => ws.send(JSON.stringify({ type: "hello", lastSeq: this.lastSeq }));
    ws.onmessage = (e) => {
      try {
        this.onMessage(JSON.parse(e.data));
      } catch (err) {
        console.error("bad sync message", err);
      }
    };
    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.ws = null;
      presence.attach(null);
      presence.clear();
      this.status = "offline";
      this.emit();
      if (this.started && !this.resyncing) {
        const delay = Math.min(15_000, 400 * 2 ** this.retry++) + Math.random() * 300;
        setTimeout(() => this.connect(), delay);
      }
    };
  }

  private onMessage(msg: { type: string } & Record<string, unknown>) {
    switch (msg.type) {
      case "ready":
        this.retry = 0;
        this.status = "live";
        this.lastSeq = Math.max(this.lastSeq, msg.seq as number);
        this.prunePending();
        presence.attach((m) => this.ws?.readyState === WebSocket.OPEN && this.ws.send(JSON.stringify(m)));
        this.emit();
        break;
      case "delta":
        this.deltaQueue.push(msg as unknown as Delta);
        if (!this.flushScheduled) {
          this.flushScheduled = true;
          queueMicrotask(() => this.flushDeltas());
        }
        break;
      case "resync":
        void this.resync();
        break;
      case "presence":
      case "presence.leave":
      case "presence.snapshot":
        presence.handle(msg as never);
        break;
    }
  }

  private flushDeltas() {
    this.flushScheduled = false;
    const deltas = this.deltaQueue.splice(0);
    const byEntity = new Map<EntityName, Delta[]>();
    for (const d of deltas) {
      if (d.seq <= this.lastSeq) continue;
      this.lastSeq = d.seq;
      if (d.clientMutationId) {
        this.pending = this.pending.filter((p) => !(p.cmid === d.clientMutationId && opId(p.op) === d.entityId));
      }
      let list = byEntity.get(d.entity);
      if (!list) byEntity.set(d.entity, (list = []));
      list.push(d);
    }
    this.prunePending();
    for (const [entity, list] of byEntity) {
      setTable(entity, (t) => {
        const table = t as unknown as Record<Id, Record<string, unknown>>;
        const touched = new Set<Id>();
        for (const d of list) {
          touched.add(d.entityId);
          if (d.op === "delete" || d.op === "hide") delete table[d.entityId];
          else if (d.op === "insert" || d.op === "show") table[d.entityId] = d.patch;
          else if (table[d.entityId]) table[d.entityId] = { ...table[d.entityId], ...d.patch };
        }
        for (const p of this.pending) {
          const id = opId(p.op);
          if (id && touched.has(id) && opEntity(p.op) === entity) applyOp(table, p.op, this.me);
        }
        return t;
      });
      if (entity === "comment" || entity === "projectUpdate" || entity === "attachment") {
        // History views refetch so the activity feed stays live.
        queryClient.invalidateQueries({ queryKey: ["history"] });
      }
    }
    if (byEntity.has("issue")) queryClient.invalidateQueries({ queryKey: ["history"] });
    this.emit();
  }

  private prunePending() {
    this.pending = this.pending.filter((p) => p.confirmedAt === undefined || p.confirmedAt > this.lastSeq);
  }

  private replayAllPending() {
    for (const p of this.pending) {
      const e = opEntity(p.op);
      if (e) setTable(e, (t) => (applyOp(t as never, p.op, this.me), t));
    }
  }

  /** Full reload after the server says this tab can't catch up. */
  async resync() {
    if (this.resyncing) return;
    this.resyncing = true;
    const ws = this.ws;
    this.ws = null;
    ws?.close();
    try {
      await this.bootstrap();
      await queryClient.invalidateQueries({ queryKey: ["lazy"] });
    } catch (e) {
      console.error("resync failed", e);
    } finally {
      this.resyncing = false;
      this.connect();
    }
  }

  // ---- writing --------------------------------------------------------------------------

  /** Applies ops locally now and sends them in order. Resolves with the server's result. */
  mutate(ops: Op[]): Promise<BatchResult> {
    const cmid = uuid();
    for (const op of ops) {
      if (op.op === "action") continue;
      this.pending.push({ cmid, op });
      setTable(op.entity, (t) => (applyOp(t as never, op, this.me), t));
    }
    const promise = new Promise<BatchResult>((resolve, reject) => {
      this.outbox.push({ cmid, ops, attempts: 0, resolve, reject });
    });
    this.emit();
    void this.drain();
    return promise;
  }

  private async drain() {
    if (this.sending) return;
    this.sending = true;
    while (this.outbox.length) {
      const entry = this.outbox[0];
      try {
        const res = await post<BatchResult>("/api/mutations", { clientMutationId: entry.cmid, ops: entry.ops });
        this.outbox.shift();
        for (const p of this.pending) if (p.cmid === entry.cmid) p.confirmedAt = res.lastSeq;
        this.prunePending();
        entry.resolve(res);
      } catch (err) {
        const status = err instanceof ApiError ? err.status : 0;
        const retryable = status === 0 || status === 429 || status >= 500;
        if (retryable && entry.attempts < 10) {
          entry.attempts++;
          this.emit();
          await sleep(Math.min(20_000, 500 * 2 ** entry.attempts));
          continue;
        }
        this.outbox.shift();
        this.rollback(entry);
        entry.reject(err);
        if (status === 401) location.assign("/login");
        else toast.error(err instanceof Error ? err.message : "That change didn't save.");
      }
      this.emit();
    }
    this.sending = false;
    this.emit();
  }

  /** Undo a rejected batch: drop its pending edits and reload server truth. */
  private rollback(entry: OutboxEntry) {
    this.pending = this.pending.filter((p) => p.cmid !== entry.cmid);
    for (const op of entry.ops) {
      if (op.op === "create") setTable(op.entity, (t) => (delete t[op.id], t));
    }
    void this.resync();
  }

  getMe() {
    return this.me;
  }
}

export const sync = new SyncEngine();

/** True when there's local state the server hasn't confirmed yet. */
export function hasUnsaved() {
  return sync.getSnapshot().pending > 0;
}

export function tableRows<E extends EntityName>(e: E) {
  return Object.values(getTable(e));
}
